
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { Button, Buttons, Drawer, Message, Messages } from "capitalrx-components-2.0/lib/general";
import { Dropdown } from "capitalrx-components-2.0/lib/forms";
import { Shelf, Stack, useResponsiveDisplayMode } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";

import { DrugResultCard } from "./DrugResultCard";
import { DrugSearchBox } from "./DrugSearchBox";

import styles from "./DrugCoverage.module.scss";

import { formularyGetByMemberAPI, formularyListByIdAPI } from "@/api";
import { MedicalEligibilityResponse } from "@/api/interfaces/Medical";

type SortOption = "tier" | "dosage";
type RestrictionFilter = "all" | "with" | "none";

interface DrugCoverageFormData {
  ndc: string;
  sort_by?: SortOption;
  restriction_filter?: RestrictionFilter;
  type_filter?: string;
  form_filter?: string;
  dosage_filter?: string;
}

export interface RawFormularyRecord {
  effective_date: string;
  end_date: string;
  start_date: string;
  termination_date: string;
  formulary_id: number;
  label_name: string;
  ndc: string;
  tier: string;
  tier_original?: string;
  brand_name?: string;
  generic_name?: string;
  custom?: string;
  gender_limit?: string;
  age_limit_original?: string;
  network?: string;
  edits?: string[];
  quantity_limit?: string;
  [key: string]: unknown;
}

interface FormularyListResponseItem {
  formulary_record_new: RawFormularyRecord;
}

const EDIT_ATTRIBUTE_MAP: Record<string, string> = {
  quantity_limit: "QL",
  prior_authorization: "PA",
  step_therapy: "ST",
  gender_limit: "GL",
  age_limit_original: "AL1",
  custom: "C",
  specialty: "S",
  quantity_limit_custom: "QLC",
  compound_kit: "CK",
  affordable_care_act: "ACA",
  high_cost_brand: "HCB",
  high_cost_generic: "HCG",
};

function extractEdits(record: Record<string, any>): string[] {
  return Object.entries(EDIT_ATTRIBUTE_MAP)
    .filter(([key]) => {
      const val = record[key];
      return val && val !== "" && val !== "0" && val !== "false";
    })
    .map(([, code]) => code);
}

function hasAnyRestriction(record: RawFormularyRecord): boolean {
  return (record.edits || []).length > 0;
}

function resultDisplayKey(record: RawFormularyRecord): string {
  const normalizedEdits = [...(record.edits || [])].sort().join(",");
  return [
    (record.label_name || "").trim().toLowerCase(),
    record.tier || "",
    record.tier_original || "",
    record.quantity_limit || "",
    normalizedEdits,
    record.custom || "",
    record.age_limit_original || "",
    record.gender_limit || "",
  ].join("|");
}

export function isExcludedTier(record: RawFormularyRecord): boolean {
  if (record.tier === "X") {
    return true;
  }
  const original = record.tier_original || "";
  return original.includes("Excluded") || original.includes("NF");
}

function tierRank(record: RawFormularyRecord): number {
  if (isExcludedTier(record)) {
    return Number.MAX_SAFE_INTEGER;
  }
  const numeric = parseInt(record.tier, 10);
  if (isNaN(numeric)) {
    return Number.MAX_SAFE_INTEGER - 1;
  }
  return numeric;
}

function dosageValue(record: RawFormularyRecord): number {
  const match = record.label_name.match(/(\d+(?:\.\d+)?)\s*(mg|mcg|ml|unit|mbq|mci)/i);
  if (!match) {
    return Number.MAX_SAFE_INTEGER;
  }
  return parseFloat(match[1]);
}

export function drugTypeLabel(record: RawFormularyRecord): string | null {
  const name = record.tier_original || "";
  if (name.includes("Generic")) {
    return "Generic";
  }
  if (name.includes("Brand")) {
    return "Brand";
  }
  return null;
}

function drugDisplayName(record: RawFormularyRecord): string {
  const tokens = record.label_name.trim().split(/\s+/);
  return tokens[0] || record.label_name;
}

function drugTypeFilterValue(record: RawFormularyRecord): string | null {
  const type = drugTypeLabel(record);
  if (!type) {
    return null;
  }
  return `${drugDisplayName(record)}|${type}`;
}

function drugTypeFilterLabel(value: string): string {
  const parts = value.split("|");
  const name = parts[0];
  const type = parts[1];
  return `${name} (${type})`;
}

const FORM_LABEL: Record<string, string> = {
  TAB: "Tablet",
  CAP: "Capsule",
  CAPS: "Capsule",
  SOLN: "Solution",
  SUSP: "Suspension",
  PACK: "Packet",
  SOSY: "Syringe",
  INJ: "Injection",
  CREA: "Cream",
  OINT: "Ointment",
};

function parseFormToken(record: RawFormularyRecord): string | null {
  const tokens = record.label_name.trim().split(/\s+/);
  const last = tokens[tokens.length - 1];
  if (!last) {
    return null;
  }
  return last.toUpperCase();
}

function formLabel(token: string): string {
  if (FORM_LABEL[token]) {
    return FORM_LABEL[token];
  }
  return token.charAt(0) + token.slice(1).toLowerCase();
}

function parseDosageLabel(record: RawFormularyRecord): string | null {
  const match = record.label_name.match(/(\d+(?:\.\d+)?)\s*(mg|mcg|ml|unit|mbq|mci)/i);
  if (!match) {
    return null;
  }
  return `${match[1]} ${match[2].toUpperCase()}`;
}

const DRUG_SEARCH_RESULTS_PER_PAGE = 200;

export const DrugCoverage: FC<{ member: MedicalEligibilityResponse }> = ({ member }) => {
  const form = useFormContext<DrugCoverageFormData>();
  const ndcValue = useWatch({ control: form.control, name: "ndc" });

  const [results, setResults] = useState<RawFormularyRecord[]>([]);
  const [formularyId, setFormularyId] = useState<number | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchedNdc, setSearchedNdc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const displayMode = useResponsiveDisplayMode();
  const isMobile = displayMode === "mobile";

  const showDrugActions = false;
  // Feature flag: show once "Check Coverage" button is implemented
  const showCheckCoverageMessage = false;

  const sortBy = (useWatch({ control: form.control, name: "sort_by" }) as SortOption) || "tier";
  const restrictionFilter =
    (useWatch({ control: form.control, name: "restriction_filter" }) as RestrictionFilter) || "all";
  const typeFilter = (useWatch({ control: form.control, name: "type_filter" }) as string) || "all";
  const formFilter = (useWatch({ control: form.control, name: "form_filter" }) as string) || "all";
  const dosageFilter = (useWatch({ control: form.control, name: "dosage_filter" }) as string) || "all";
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const isSearchDisabled = isLoading || isSearching || !formularyId || !ndcValue;

  useEffect(() => {
    if (!form.getValues("sort_by")) {
      form.setValue("sort_by", "tier");
    }
  }, [form]);

  const handleResetFilters = useCallback(() => {
    form.setValue("type_filter", null);
    form.setValue("form_filter", null);
    form.setValue("dosage_filter", null);
    form.setValue("restriction_filter", null);
  }, [form]);
  const handleSearch = useCallback(async () => {
    const value = ndcValue?.trim();

    if (!value) {
      setErrorMessage(null);
      return;
    }

    if (isLoading) {
      setErrorMessage("Formulary data is still loading. Please try again.");
      return;
    }

    if (!formularyId) {
      setErrorMessage("Unable to load formulary data for this member.");
      return;
    }

    const isHCPCS = /^[A-V][0-9]{4}$/i.test(value);

    if (!isHCPCS && value.length < 3) {
      setErrorMessage(
        "Please enter at least 3 characters for a drug name, NDC, or GPI.",
      );
      setResults([]);
      return;
    }

    setErrorMessage(null);
    setSearchedNdc(value);

    const isNumeric = /^\d+$/.test(value);
    const filters: Record<string, string>[] = isNumeric
      ? [{ ndc: value }]
      : [{ brand_name: value }, { generic_name: value }, { label_name: value }];

    const fetchRecords = async (
      filter: Record<string, string>,
    ): Promise<RawFormularyRecord[]> => {
      const response = await formularyListByIdAPI({
        formulary_id: formularyId,
        results_per_page: DRUG_SEARCH_RESULTS_PER_PAGE,
        ...filter,
      });
      const rows: FormularyListResponseItem[] = Array.isArray(response?.results)
        ? response.results
        : [];
      return rows.map((item) => ({
        ...item.formulary_record_new,
        edits: extractEdits(item.formulary_record_new),
      }));
    };

    try {
      setIsSearching(true);

      const allRecords = (await Promise.all(filters.map(fetchRecords))).flat();

      const recordsByNdc = new Map<string, RawFormularyRecord>();
      for (const record of allRecords) {
        recordsByNdc.set(record.ndc, record);
      }

      const uniqueResults = new Map<string, RawFormularyRecord>();
      for (const record of recordsByNdc.values()) {
        const dedupeKey = resultDisplayKey(record);
        if (!uniqueResults.has(dedupeKey)) {
          uniqueResults.set(dedupeKey, record);
        }
      }

      setResults(Array.from(uniqueResults.values()));
    } catch (err) {
      console.error("Drug coverage search failed:", err);
      setErrorMessage(
        "An unexpected error occurred while searching for drug coverage.",
      );
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [ndcValue, isLoading, formularyId]);

  useEffect(() => {
    const loadFormularyData = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);

        const formularyInfo = await formularyGetByMemberAPI({
          external_member_id: member.external_member_id,
          member_first_name: member.member_first_name,
          member_last_name: member.member_last_name,
          member_date_of_birth: member.member_date_of_birth,
        });

        if (!formularyInfo?.formulary_id) {
          throw new Error("formulary_id missing from formularyInfo");
        }

        setFormularyId(formularyInfo.formulary_id);
      } catch (err) {
        console.error("Failed to load formulary data:", err);
        setFormularyId(undefined);
        setResults([]);
        setErrorMessage("Unable to load formulary data for this member.");
      } finally {
        setIsLoading(false);
      }
    };

    loadFormularyData();
  }, [member]);

  const displayResults = useMemo(() => {
    let filtered = results;

    if (typeFilter !== "all") {
      filtered = filtered.filter((record) => drugTypeFilterValue(record) === typeFilter);
    }
    if (formFilter !== "all") {
      filtered = filtered.filter((record) => parseFormToken(record) === formFilter);
    }
    if (dosageFilter !== "all") {
      filtered = filtered.filter((record) => parseDosageLabel(record) === dosageFilter);
    }

    if (restrictionFilter === "with") {
      filtered = filtered.filter((record) => hasAnyRestriction(record));
    } else if (restrictionFilter === "none") {
      filtered = filtered.filter((record) => !hasAnyRestriction(record));
    }

    const sorted = [...filtered];
    if (sortBy === "dosage") {
      sorted.sort((a, b) => dosageValue(a) - dosageValue(b));
    } else {
      sorted.sort((a, b) => tierRank(a) - tierRank(b));
    }
    return sorted;
  }, [results, typeFilter, formFilter, dosageFilter, restrictionFilter, sortBy]);

  const typeOptions = useMemo(() => {
    const values = results
      .map((record) => drugTypeFilterValue(record))
      .filter((value): value is string => value !== null);
    return Array.from(new Set(values));
  }, [results]);

  const formOptions = useMemo(() => {
    const values = results
      .map((record) => parseFormToken(record))
      .filter((value): value is string => value !== null);
    return Array.from(new Set(values));
  }, [results]);

  const dosageOptions = useMemo(() => {
    const values = results
      .map((record) => parseDosageLabel(record))
      .filter((value): value is string => value !== null);
    const unique = Array.from(new Set(values));
    unique.sort((a, b) => parseFloat(a) - parseFloat(b));
    return unique;
  }, [results]);

  const showTypeFilter = typeOptions.length > 1;
  const showFormFilter = formOptions.length > 1;
  const showDosageFilter = dosageOptions.length > 1;

  const showRestrictionFilter = useMemo(() => {
    if (results.length < 2) {
      return false;
    }
    const withRestrictions = results.some((record) => hasAnyRestriction(record));
    const withoutRestrictions = results.some((record) => !hasAnyRestriction(record));
    return withRestrictions && withoutRestrictions;
  }, [results]);

  const anyFilterShown =
    showTypeFilter || showFormFilter || showDosageFilter || showRestrictionFilter;

  return (!isLoading &&
    <Stack spacing="medium-large">
      <Stack>
        <Stack spacing="mini">
          <DrugSearchBox testId="test__drug_coverage_ndc_dropdown" formularyId={formularyId} />
          <Body size="mini" className={styles.SearchHint}>
            Search with a minimum of 3 characters for drug name, NDC/GPI, or exact match for HCPCS code
          </Body>
        </Stack>
        <Shelf>
          <Button
            appearance="primary"
            disabled={isSearchDisabled}
            onClick={(e: any) => {
              e?.preventDefault?.();
              handleSearch();
            }}
          >
            {isSearching ? "Searching..." : "Search"}
          </Button>
          <Shelf.Spacer />
        </Shelf>
      </Stack>

      {errorMessage && (
        <Message appearance={Messages.Error} visible>
          {errorMessage}
        </Message>
      )}

      {searchedNdc &&
        results.length === 0 &&
        !isSearching &&
        !errorMessage &&
        <Message appearance={Messages.Warning} visible>
          This drug is not part of the member's formulary.
        </Message>
      }

      {results.length > 0 && (
        <Stack spacing="medium">
          <Semibold className={styles.ResultsHeading}>
            Showing Results for "{searchedNdc}"
          </Semibold>

          {showCheckCoverageMessage && (
            <Message appearance={Messages.Informational} visible>
              The results shown are based on formulary review only. To check
              plan-specific coverage and estimated out-of-pocket cost for this
              patient, click on Check Coverage.
            </Message>
          )}

          {isMobile ? (
            <>
              <Shelf spacing="small">
                {anyFilterShown && (
                  <Button
                    appearance="secondary"
                    icon="filter"
                    onClick={() => setIsFilterDrawerOpen(true)}
                  >
                    Filters
                  </Button>
                )}
                <Shelf.Spacer />
                <Body className={styles.SortLabel}>
                  Sort by:
                </Body>
                <div className={styles.MobileSortDropdown}>
                  <Dropdown.Field name="sort_by" label="Sort by" hideLabel required>
                    <Dropdown.Option value="tier">Tier (Low to High)</Dropdown.Option>
                    <Dropdown.Option value="dosage">Dosage (Low to High)</Dropdown.Option>
                  </Dropdown.Field>
                </div>
              </Shelf>
              <Drawer
                visible={isFilterDrawerOpen}
                onClose={() => setIsFilterDrawerOpen(false)}
                title="Filter"
                footer={
                  <Shelf spacing="small">
                    <Button appearance={Buttons.Functional.Tertiary.Large} onClick={handleResetFilters}>
                      Reset
                    </Button>
                    <Shelf.Spacer />
                    <Button appearance={Buttons.Functional.Tertiary.Large} onClick={() => setIsFilterDrawerOpen(false)}>
                      Cancel
                    </Button>
                    <Button appearance={Buttons.Functional.Primary.Large} onClick={() => setIsFilterDrawerOpen(false)}>
                      Filter
                    </Button>
                  </Shelf>
                }
              >
                <Stack spacing="medium">
                  {showTypeFilter && (
                    <Dropdown.Field className={styles.Filter} name="type_filter" label="Type" hideLabel placeholder="Type">
                      {typeOptions.map((value) => (
                        <Dropdown.Option key={value} value={value}>
                          {drugTypeFilterLabel(value)}
                        </Dropdown.Option>
                      ))}
                    </Dropdown.Field>
                  )}
                  {showFormFilter && (
                    <Dropdown.Field className={styles.Filter} name="form_filter" label="Form" hideLabel placeholder="Form">
                      {formOptions.map((token) => (
                        <Dropdown.Option key={token} value={token}>
                          {formLabel(token)}
                        </Dropdown.Option>
                      ))}
                    </Dropdown.Field>
                  )}
                  {showDosageFilter && (
                    <Dropdown.Field className={styles.Filter} name="dosage_filter" label="Dosage" hideLabel placeholder="Dosage">
                      {dosageOptions.map((dose) => (
                        <Dropdown.Option key={dose} value={dose}>
                          {dose}
                        </Dropdown.Option>
                      ))}
                    </Dropdown.Field>
                  )}
                  {showRestrictionFilter && (
                    <Dropdown.Field className={styles.Filter} name="restriction_filter" label="Restrictions" hideLabel placeholder="Restrictions">
                      <Dropdown.Option value="with">With Restrictions</Dropdown.Option>
                      <Dropdown.Option value="none">No Restrictions</Dropdown.Option>
                    </Dropdown.Field>
                  )}
                </Stack>
              </Drawer>
            </>
          ) : (
            <Shelf spacing="small" className={styles.FilterBar}>
              {showTypeFilter && (
                <div className={styles.Filter}>
                  <Dropdown.Field name="type_filter" label="Type" hideLabel placeholder="Type">
                    {typeOptions.map((value) => (
                      <Dropdown.Option key={value} value={value}>
                        {drugTypeFilterLabel(value)}
                      </Dropdown.Option>
                    ))}
                  </Dropdown.Field>
                </div>
              )}
              {showFormFilter && (
                <div className={styles.Filter}>
                  <Dropdown.Field name="form_filter" label="Form" hideLabel placeholder="Form">
                    {formOptions.map((token) => (
                      <Dropdown.Option key={token} value={token}>
                        {formLabel(token)}
                      </Dropdown.Option>
                    ))}
                  </Dropdown.Field>
                </div>
              )}
              {showDosageFilter && (
                <div className={styles.Filter}>
                  <Dropdown.Field name="dosage_filter" label="Dosage" hideLabel placeholder="Dosage">
                    {dosageOptions.map((dose) => (
                      <Dropdown.Option key={dose} value={dose}>
                        {dose}
                      </Dropdown.Option>
                    ))}
                  </Dropdown.Field>
                </div>
              )}
              {showRestrictionFilter && (
                <div className={styles.Filter}>
                  <Dropdown.Field name="restriction_filter" label="Restrictions" hideLabel placeholder="Restrictions">
                    <Dropdown.Option value="with">With Restrictions</Dropdown.Option>
                    <Dropdown.Option value="none">No Restrictions</Dropdown.Option>
                  </Dropdown.Field>
                </div>
              )}
              <Shelf.Spacer />
              <Shelf spacing="small" className={styles.FilterBar}>
                <Body className={styles.SortLabel}>
                  Sort by:
                </Body>
                <Dropdown.Field className={styles.Filter} name="sort_by" label="Sort by" hideLabel required>
                  <Dropdown.Option value="tier">Tier (Low to High)</Dropdown.Option>
                  <Dropdown.Option value="dosage">Dosage (Low to High)</Dropdown.Option>
                </Dropdown.Field>
              </Shelf>
            </Shelf>
          )}

          <Stack spacing="medium" className={styles.ResultCards}>
            {displayResults.map((result) => (
              <DrugResultCard
                key={result.ndc}
                record={result}
                isMobile={isMobile}
                showDrugActions={showDrugActions}
              />
            ))}
          </Stack>
        </Stack >
      )
      }
    </Stack >
  );
};
